import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/apiClient";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Moon, Music, Sparkles, Wand2 } from "lucide-react";
import { toast } from "sonner";

const STATUS = { draft: "Draft — review, then approve", rendering: "Making audio… (a few minutes)", writing: "Writing scripts… (about a minute)", ready: "Live", failed: "Failed — try approving again" };

function Row({ it, onData }) {
  const [day, setDay] = useState(it.long_script || "");
  const [sleep, setSleep] = useState(it.sleep_script || "");
  const [busy, setBusy] = useState("");
  useEffect(() => { setDay(it.long_script || ""); setSleep(it.sleep_script || ""); }, [it.long_script, it.sleep_script]);
  const call = async (kind, fn) => {
    setBusy(kind);
    try { const { data } = await fn(); onData(data); } catch (e) { toast.error(e?.response?.data?.detail || "Something went wrong"); }
    setBusy("");
  };
  const words = (t) => (t.trim() ? t.trim().split(/\s+/).length : 0);
  const dirty = day !== (it.long_script || "") || sleep !== (it.sleep_script || "");
  return (
    <div className="rounded-2xl border border-border bg-card/40 p-5" data-testid={`long-${it.id}`}>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div>
          <p className="font-serif text-xl">{it.title}</p>
          <p className="text-xs text-muted-foreground" data-testid={`long-status-${it.id}`}>
            {it.long_status ? STATUS[it.long_status] : "No full-length version yet"}
            {it.long_status === "ready" && ` · day ${it.long_duration} · sleep ${it.sleep_duration}`}
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full" disabled={!!busy || it.long_status === "writing" || it.long_status === "rendering"} onClick={() => call("draft", () => api.post(`/admin/long-installations/${it.id}/draft`))} data-testid={`long-draft-${it.id}`}>
          {busy === "draft" || it.long_status === "writing" ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Writing…</> : <><Wand2 className="w-4 h-4 mr-1.5" /> {it.long_script ? "Rewrite" : "Write scripts"}</>}
        </Button>
      </div>
      {(it.long_script || it.sleep_script) && (
        <>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Sparkles className="w-3 h-3" /> Day version · {words(day)} words</p>
              <Textarea value={day} onChange={(e) => setDay(e.target.value)} rows={10} className="text-sm" data-testid={`long-day-${it.id}`} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 flex items-center gap-1"><Moon className="w-3 h-3" /> Sleep version · {words(sleep)} words</p>
              <Textarea value={sleep} onChange={(e) => setSleep(e.target.value)} rows={10} className="text-sm" data-testid={`long-sleep-${it.id}`} />
            </div>
          </div>
          <p className="text-[11px] text-muted-foreground mt-2">A blank line between paragraphs = a longer 3–4 second pause.</p>
          <div className="flex gap-2 mt-3">
            {dirty && <Button variant="outline" size="sm" className="rounded-full" disabled={!!busy} onClick={() => call("save", () => api.put(`/admin/long-installations/${it.id}`, { long_script: day, sleep_script: sleep }))} data-testid={`long-save-${it.id}`}>Save edits</Button>}
            <Button size="sm" className="rounded-full" disabled={!!busy || dirty || it.long_status === "rendering"} onClick={() => call("approve", () => api.post(`/admin/long-installations/${it.id}/approve`))} data-testid={`long-approve-${it.id}`}>
              {it.long_status === "rendering" ? <><Loader2 className="w-4 h-4 animate-spin mr-1.5" /> Making audio…</> : "Approve & make audio"}
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

export default function AdminLongInstallations() {
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const load = () => api.get("/admin/long-installations").then((r) => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  useEffect(() => {
    if (!data?.items.some((i) => ["rendering", "writing"].includes(i.long_status))) return;
    const t = setInterval(load, 8000); return () => clearInterval(t);
  }, [data]);
  if (!data) return null;

  const uploadMusic = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const up = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const { data: d } = await api.post("/admin/sleep-music", { file_id: up.data.file_id }); setData(d); toast.success("Sleep music set");
    } catch { toast.error("Upload failed"); }
    setUploading(false);
  };

  return (
    <div className="mt-14 pt-10 border-t border-border" data-testid="admin-long">
      <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Inner Architecture</p>
      <h2 className="font-serif text-3xl sm:text-4xl mb-2">Full-length & Sleep installations</h2>
      <a href="/admin/scripts" className="inline-block text-sm text-primary hover:underline mb-3" data-testid="admin-scripts-review-link">Read all scripts comfortably →</a>
      <p className="text-muted-foreground mb-5 max-w-2xl">The coach writes 8–10 minute versions in the style of your own recording. Review and edit each one, then approve: Adelaide voices it slowly, and the sleep version drifts into music and fades out.</p>
      <div className="flex flex-wrap items-center gap-3 mb-6 text-sm" data-testid="sleep-music">
        <Music className="w-4 h-4 text-primary" />
        <span>Sleep music: <b>{data.sleep_music?.name || "Built-in soft ambient pad"}</b></span>
        <input ref={fileRef} type="file" accept="audio/*" className="hidden" onChange={(e) => e.target.files[0] && uploadMusic(e.target.files[0])} data-testid="sleep-music-input" />
        <Button size="sm" variant="outline" className="rounded-full" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="sleep-music-upload">
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Upload Soulumo track"}
        </Button>
      </div>
      <div className="space-y-4">{data.items.map((it) => <Row key={it.id} it={it} onData={setData} />)}</div>
    </div>
  );
}
